// Shared button preset library — used by BOTH the admin Templates page (to
// let admins configure a fully custom set of buttons per item) and the
// photographer ChecklistCard (to render them). Keep the icon/title mapping in
// ONE place so the "Photographer Preview" in the admin renders the exact
// icon+label the photographer will see.
import {
  BookOpen,
  Calendar,
  Download,
  Eye,
  FileSignature,
  GraduationCap,
  Info,
  Phone,
  Printer,
  Upload,
  Wrench,
} from "lucide-react";

// Curated preset titles — each carries an icon + sensible defaults for action
// and color. Admin can override color and (if action="url") the URL.
// `defaultAction: "upload"` triggers the file picker inside the ChecklistCard;
// everything else opens the button's URL in a new tab.
export const BUTTON_PRESETS = [
  { title: "Upload",          icon: Upload,         defaultAction: "upload", defaultColor: "primary"   },
  { title: "Download",        icon: Download,       defaultAction: "url",    defaultColor: "primary"   },
  { title: "Download & Sign", icon: Download,       defaultAction: "url",    defaultColor: "primary"   },
  { title: "View",            icon: Eye,            defaultAction: "url",    defaultColor: "primary"   },
  { title: "View Document",   icon: Eye,            defaultAction: "url",    defaultColor: "primary"   },
  { title: "Sign",            icon: FileSignature,  defaultAction: "url",    defaultColor: "primary"   },
  { title: "Print",           icon: Printer,        defaultAction: "url",    defaultColor: "secondary" },
  { title: "Learn More",      icon: BookOpen,       defaultAction: "url",    defaultColor: "secondary" },
  { title: "View Details",    icon: Info,           defaultAction: "url",    defaultColor: "secondary" },
  { title: "Book Call",       icon: Phone,          defaultAction: "url",    defaultColor: "primary"   },
  { title: "Schedule",        icon: Calendar,       defaultAction: "url",    defaultColor: "primary"   },
  { title: "Set Up",          icon: Wrench,         defaultAction: "url",    defaultColor: "primary"   },
  { title: "Take Exam",       icon: GraduationCap,  defaultAction: "url",    defaultColor: "primary"   },
];

export function presetFor(title) {
  if (!title) return null;
  const q = title.trim().toLowerCase();
  return BUTTON_PRESETS.find((p) => p.title.toLowerCase() === q) || null;
}

// Returns the lucide icon component to render alongside the label. Falls back
// to Eye so a custom label the admin typed still gets *some* icon rather than
// a bare wordmark.
export function iconForTitle(title) {
  const p = presetFor(title);
  return p ? p.icon : Eye;
}

export const BUTTON_COLORS = [
  { key: "primary",   label: "Oxblood",     swatch: "#6B0F1A" },
  { key: "secondary", label: "Clear",       swatch: "#F5EBD9" },
  { key: "gold",      label: "Gold",        swatch: "#B08D57" },
];

// Compute the Tailwind classes for a button color. Kept as a switch (not a
// map) so the strings stay literal — Tailwind's JIT purger needs to see the
// exact class strings at build time.
export function buttonColorClasses(color) {
  switch (color) {
    case "gold":
      return "bg-[#B08D57] hover:bg-[#6B5530] text-[#F5EBD9] border border-transparent";
    case "secondary":
      return "bg-transparent text-[#6B0F1A] hover:bg-[#6B0F1A]/8 border border-[#6B0F1A]/40";
    case "primary":
    default:
      return "bg-[#6B0F1A] hover:bg-[#8A1424] text-[#F5EBD9] btn-primary-glow border border-transparent";
  }
}

// Enforce the single canonical host: `www.onboarding.goreelty.com`. The bare
// apex is intentionally retired — no DNS record, no cert, no server behind it.
// If an admin pastes a legacy bare-apex URL (from an older doc/email) or the
// DB still has one from before the flip, we silently upgrade it to the www
// variant on save so photographers always click a URL that resolves.
// Unrelated URLs (Docsend, Dropbox, Calendly, external sites) are untouched.
export function stripWwwFromUrl(raw) {
  if (!raw || typeof raw !== "string") return raw;
  const trimmed = raw.trim();
  if (!trimmed) return trimmed;
  // Only rewrite when the host is EXACTLY `onboarding.goreelty.com`. The
  // `(?=[/:?#]|$)` boundary blocks lookalikes like `onboarding.goreelty.com.evil.com`.
  return trimmed.replace(
    /^(https?:\/\/)onboarding\.goreelty\.com(?=[/:?#]|$)/i,
    "$1www.onboarding.goreelty.com"
  );
}

// Normalize a raw button config coming from the DB or admin form. Ensures
// every field has a sane default so rendering never NPEs on partial data.
// `_key` is a client-only stable identifier so React reconciliation survives
// reorders (swap-up/swap-down) without losing input focus. Stripped by
// `buildPayload` before the backend ever sees it.
export function normalizeButton(raw) {
  const title = (raw?.title || "").trim();
  const preset = presetFor(title);
  return {
    _key: raw?._key || (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `btn-${Date.now()}-${Math.random().toString(36).slice(2)}`),
    title,
    color: raw?.color || preset?.defaultColor || "primary",
    action: raw?.action || preset?.defaultAction || "url",
    url: stripWwwFromUrl(raw?.url || ""),
  };
}
